/*
  everaw-webmcp.js — Everaw's own tools for AI agents, via WebMCP.

  WHY THIS EXISTS
  ---------------
  Shopify registers ten WebMCP tools on every Liquid storefront automatically
  (search_catalog, get_cart, update_cart, proceed_to_checkout, manage_orders and
  friends). They are good at commerce and blind to everything that makes this a
  kids nutrition brand.

  Audited 2026-08-27: an agent asked "does this contain nuts, my child is
  allergic" gets NOTHING from the Shopify surface. get_product_details returns
  no metafields at all, so shopify.allergen-information never reaches an agent,
  and zero of 26 product descriptions mentioned allergens. The allergen data
  exists, in assets/llmFeed.json, and no tool could read it.

  These tools close that gap. They are additive: different names, no attempt to
  wrap or override anything Shopify registers.

  DESIGN NOTES
  ------------
  - Zero cost to a normal shopper. Nothing is fetched, and nothing is even
    registered, unless the browser exposes document.modelContext. Today that is
    Chrome 149+/Edge 150+ in the origin trial and ChatGPT Desktop.
  - llmFeed.json is fetched ONCE, lazily, on first tool invocation, then cached
    on the module. A visitor whose agent never calls a tool pays nothing.
  - Every tool is readOnlyHint: true. None of them mutate anything. Cart and
    checkout stay entirely with Shopify's tools, which keeps the buyer-approval
    invariant where it belongs.
  - Outputs are capped. Chrome's guidance is roughly 500 chars of description
    and 1.5K per tool result; blowing past that trips agent guardrails.
  - NUTRITION IS DELIBERATELY NOT EXPOSED HERE. As of 2026-08-27 six products
    have an unresolved conflict between the printed pack panel and an accredited
    lab report, so per-serving values are not something we should be handing to
    an assistant as fact. See the pack-vs-lab reconciliation in everaw-os.
    Add a nutrition tool once that is closed out, not before.
*/
(function () {
  'use strict';

  function ctx() {
    if (typeof document !== 'undefined' && 'modelContext' in document) return document.modelContext || null;
    if (typeof navigator !== 'undefined' && 'modelContext' in navigator) return navigator.modelContext || null;
    return null;
  }

  var mc = ctx();
  if (!mc || typeof mc.registerTool !== 'function') return;   // not an agent browser, do nothing

  /* Generated by tools/build_webmcp_enums.py from the live feed. webmcp.com's
     graded report (29 Aug 2026) flagged "product is unconstrained free text" on
     three of our four tools. We sell an enumerable number of products, so the
     agent should pick from a list rather than guess a name.
     Re-run that script after any catalogue change; a stale enum would reject a
     product we actually sell. */
  /* PRODUCT-ENUM-START */
  var PRODUCT_NAMES = ["Activated Almond Butter", "Activated Cashew Butter", "Assorted Box", "Blue Smoothie", "Blue Vanilla Cashew Butter", "Brown Smoothie", "Cocoa Almond Butter", "Freeze-Dried Blueberry", "Freeze-Dried Kala Jamun", "Freeze-Dried Mango", "Freeze-Dried Mulberry", "Freeze-Dried Pineapple", "Freeze-Dried Strawberry", "Green Smoothie", "Mango Almond Butter", "Matcha Coconut Butter", "Pineapple Cashew Butter", "Pink Smoothie", "Roasted Hazelnut Butter", "Smoothie Mix - Pack of 5", "Sprouted Peanut Butter", "Strawberry Cashew Butter", "Super Spread - Assorted Pack (5 x 100g)", "Yellow Smoothie"];
  /* PRODUCT-ENUM-END */
  function productEnum(withAll) {
    var e = PRODUCT_NAMES.slice();
    if (withAll) e.push('all');
    return e.length > 1 ? e : undefined;   // omit the key entirely if unbuilt
  }

  var script = document.currentScript;
  var FEED_URL = (script && script.getAttribute('data-feed')) || '/llmfeed.json';
  var feed = null, feedPromise = null;

  function load() {
    if (feed) return Promise.resolve(feed);
    if (feedPromise) return feedPromise;
    feedPromise = fetch(FEED_URL, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('feed ' + r.status); return r.json(); })
      .then(function (d) { feed = d; return d; });
    return feedPromise;
  }

  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }

  function match(products, q) {
    var n = norm(q);
    if (!n) return [];
    var exact = products.filter(function (p) { return norm(p.name) === n || norm(p.handle) === n; });
    if (exact.length) return exact;
    return products.filter(function (p) {
      return norm(p.name).indexOf(n) !== -1 || norm(p.handle).indexOf(n) !== -1 ||
             norm(p.ingredients).indexOf(n) !== -1;
    });
  }

  var ALLERGEN_LABEL = { tree_nut: 'tree nuts', peanut: 'peanuts', milk: 'milk', soy: 'soy' };

  function allergenLine(p) {
    var a = (p.allergens || []).map(function (x) { return ALLERGEN_LABEL[x] || x; });
    var s = a.length ? 'Contains ' + a.join(' and ') + '.' : 'Contains no nuts as an ingredient.';
    if (p.facility) s += ' ' + p.facility;
    return s;
  }

  /* Truncate on a LINE boundary, never mid-token. Cutting inside a URL hands an
     agent a broken link it will happily quote; the 'kid-friendly' list tripped
     exactly that in testing, ending "...products/h". */
  function cap(s, n) {
    if (s.length <= n) return s;
    var cut = s.slice(0, n - 60);
    var nl = cut.lastIndexOf('\n');
    if (nl > 0) cut = cut.slice(0, nl);
    return cut + '\n(list truncated, see https://everaw.in/collections/all for the full range)';
  }

  mc.registerTool({
    name: 'everaw_check_allergens',
    description: 'Check which allergens an Everaw product contains, and whether it is made in a facility that handles nuts. Use this whenever someone asks about nuts, peanuts, allergies or whether a product is safe for an allergic child. Pass a product name, or "all" to list every product. Returns plain text: product name, an allergen line naming tree nuts or peanuts where present, the shared-facility statement, and ingredients. For "all", two named groups plus that statement.',
    annotations: { readOnlyHint: true },
    inputSchema: {
      type: 'object',
      properties: { product: { type: 'string', enum: productEnum(true), description: 'Product name, or "all" for the whole range.' } },
      required: ['product']
    },
    execute: function (input) {
      return load().then(function (d) {
        var ps = d.products || [];
        if (norm(input && input.product) === 'all') {
          var withN = ps.filter(function (p) { return (p.allergens || []).length; });
          var without = ps.filter(function (p) { return !(p.allergens || []).length; });
          return cap(
            'Every Everaw product is made in a facility that also processes nuts.\n\n' +
            'CONTAIN NUTS AS AN INGREDIENT (' + withN.length + '): ' +
            withN.map(function (p) { return p.name; }).join(', ') + '.\n\n' +
            'NO NUTS AS AN INGREDIENT (' + without.length + '), but same facility: ' +
            without.map(function (p) { return p.name; }).join(', ') + '.\n\n' +
            'For a child with a diagnosed nut allergy, the facility statement applies to the whole range.', 1500);
        }
        var hits = match(ps, input && input.product);
        if (!hits.length) return 'No Everaw product matches "' + String(input && input.product).slice(0, 60) + '". Use "all" to list the range.';
        return cap(hits.slice(0, 4).map(function (p) {
          return p.name + '\n  ' + allergenLine(p) + '\n  Ingredients: ' + p.ingredients;
        }).join('\n\n'), 1500);
      });
    }
  });

  mc.registerTool({
    name: 'everaw_find_by_dietary_need',
    description: 'Find Everaw products matching a dietary requirement: no nuts, no added sugar, vegan, gluten free, or suitable for children. Use this when someone describes a need rather than naming a product, for example "something nut free for my toddler". Returns plain text: for no-nuts the shared-facility caveat comes FIRST, then one line per match as "- Name (price INR): url". Relay the caveat, it qualifies every item listed.',
    annotations: { readOnlyHint: true },
    inputSchema: {
      type: 'object',
      properties: {
        need: {
          type: 'string',
          /* enum, not prose. webmcp.com graded the surface on 2026-08-29 and
             flagged "loose enums on Everaw tools": the five valid values were
             described in the description string, so an agent had to parse
             English to learn them and could not be validated against them.
             JSON Schema enum makes the set machine-readable and lets the
             browser reject a bad call before execute() ever runs. */
          enum: ['no-nuts', 'no-added-sugar', 'vegan', 'gluten-free', 'kid-friendly'],
          description: 'The dietary requirement to match against.'
        }
      },
      required: ['need']
    },
    execute: function (input) {
      return load().then(function (d) {
        /* Still matched loosely even though the schema now declares an enum:
           the enum constrains a well-behaved caller, it does not guarantee one,
           and an agent passing "nut free" or "kid friendly" should still get an
           answer rather than a schema error. */
        var need = norm(input && input.need).replace(/ /g, '-');
        var ps = d.products || [], out, note = '';
        if (need.indexOf('nut') !== -1) {
          out = ps.filter(function (p) { return !(p.allergens || []).length; });
          note = 'These contain no nuts as an ingredient. All Everaw products are made in a facility that also processes nuts, so this is not suitable for a diagnosed nut allergy without checking with us first.';
        } else if (need.indexOf('kid') !== -1 || need.indexOf('child') !== -1 || need.indexOf('toddler') !== -1) {
          out = ps.filter(function (p) { return p.kid_friendly; });
        } else {
          var key = need.indexOf('sugar') !== -1 ? 'no added sugar'
                  : need.indexOf('vegan') !== -1 ? 'vegan'
                  : need.indexOf('gluten') !== -1 ? 'gluten-free' : null;
          if (!key) return 'Supported needs: no-nuts, no-added-sugar, vegan, gluten-free, kid-friendly.';
          out = ps.filter(function (p) { return norm(p.dietary_claims).indexOf(norm(key)) !== -1; });
        }
        if (!out.length) return 'No products matched that requirement.';
        /* The caveat leads. It used to trail the list, 87% of the way into the
           response, and webmcp.com's live agent run summarised the result as
           "10 nut-free products" -- the exact claim we do not make, because
           everything is made on shared equipment. A qualifier an agent has to
           reach the end to find is the first thing summarisation drops. */
        return cap((note ? note + '\n\n' : '') + out.map(function (p) {
          return '- ' + p.name + ' (' + p.price + ' INR): ' + p.canonical_url;
        }).join('\n'), 1500);
      });
    }
  });

  mc.registerTool({
    name: 'everaw_lab_reports',
    description: 'Report whether an Everaw product has been independently laboratory tested, and point to the published reports. Use this when someone asks if the products are tested, verified, third party checked, or whether the published nutrition can be trusted. Pass a product name, or "all" for the range. Returns plain text: whether the product is independently tested, the report date if any, and the URL of the published reports. Says so plainly when none exists.',
    annotations: { readOnlyHint: true },
    inputSchema: {
      type: 'object',
      properties: { product: { type: 'string', enum: productEnum(true), description: 'Product name, or "all".' } },
      required: ['product']
    },
    execute: function (input) {
      return load().then(function (d) {
        var ps = d.products || [];
        var SCOPE = 'Reports in full: https://everaw.in/pages/lab-reports';
        /* The feed no longer publishes report numbers, so identity comes from
           measured_by, which the generator sets to exactly this string for a
           tested product and to a "pack panel, no independent report" string
           otherwise. Exact match on purpose: both contain the word
           "independent". */
        function tested(p) {
          return (p.nutrition || {}).measured_by === 'independent laboratory testing';
        }
        if (norm(input && input.product) === 'all') {
          var t = ps.filter(tested), u = ps.filter(function (p) { return !tested(p); });
          return cap(
            'Everaw publishes its independent lab reports in full, unedited, at '
            + 'https://everaw.in/pages/lab-reports\n\n'
            + 'INDEPENDENTLY TESTED (' + t.length + '): '
            + t.map(function (p) { return p.name; }).join(', ') + '.\n\n'
            + (u.length ? 'NO REPORT YET (' + u.length + '): '
                + u.map(function (p) { return p.name; }).join(', ') + '.\n\n' : '')
            + 'The per-serving nutrition Everaw publishes is the lab figure, not the '
            + 'printed pack panel, wherever a report exists.\n\n' + SCOPE, 1500);
        }
        var hits = match(ps, input && input.product);
        if (!hits.length) return 'No Everaw product matches that name. Use "all" to list the range.';
        var p = hits[0], n = p.nutrition || {}, r = n.report || {};
        if (!tested(p)) {
          return cap(p.name + '\nNo independent lab report for this product yet. Its published '
            + 'nutrition comes from the printed pack panel.\n\n' + SCOPE, 1500);
        }
        return cap([
          p.name,
          'Independently laboratory tested.',
          r.report_date ? 'Report dated ' + r.report_date : '',
          'The nutrition Everaw publishes for this product is the lab figure, not the pack panel.',
          '',
          SCOPE
        ].filter(Boolean).join('\n'), 1500);
      });
    }
  });

  mc.registerTool({
    name: 'everaw_product_facts',
    description: 'Get the factual details of one Everaw product: ingredients, allergens, net weight, serving size, shelf life and storage. Use this for questions about what is in a product or how long it keeps. Does not cover price or stock, which come from the Shopify catalog tools. Returns plain text, one field per line: name, ingredients, allergens with the facility statement, dietary claims, weight and serving, shelf life, usage, URL.',
    annotations: { readOnlyHint: true },
    inputSchema: {
      type: 'object',
      properties: { product: { type: 'string', enum: productEnum(false), description: 'Product name.' } },
      required: ['product']
    },
    execute: function (input) {
      return load().then(function (d) {
        var hits = match(d.products || [], input && input.product);
        if (!hits.length) return 'No Everaw product matches that name.';
        var p = hits[0];
        return cap([
          p.name,
          'Ingredients: ' + p.ingredients,
          allergenLine(p),
          'Dietary: ' + p.dietary_claims,
          'Net weight: ' + p.net_weight + '. Serving: ' + p.serving + '.',
          'Shelf life: ' + p.shelf_life,
          'Usage: ' + p.usage,
          p.canonical_url
        ].join('\n'), 1500);
      });
    }
  });
})();
